import { useCallback, useEffect, useMemo, useState } from 'react';
import * as THREE from 'three';
import { EDIT_ACTIVE_COLOR } from '../editMode.constants';
import { isSupportPartId } from '../partialRenest.utils';
import { getIdlePartColor, LayerData, setPalletChildrenVisibility } from './usePartLoading';

// Layers that hold at least one step. Layer numbers can skip entirely-empty layers, and a layer can
// hold dunnage but no parts (the engine laid dunnage for a part it then excluded), so the
// next/previous layer with a step isn't necessarily currentLayer +/- 1.
function getPopulatedLayerKeys(layerData: { [key: number]: LayerData }): number[] {
  return Object.keys(layerData)
    .map(Number)
    .filter((layerIndex) => (layerData[layerIndex]?.parts?.length ?? 0) > 0)
    .sort((a, b) => a - b);
}

function findAdjacentPopulatedLayer(
  layerData: { [key: number]: LayerData },
  currentLayer: number,
  direction: 1 | -1
): number | undefined {
  const layerKeys = getPopulatedLayerKeys(layerData);
  if (direction === 1) {
    return layerKeys.find((layerIndex) => layerIndex > currentLayer);
  }
  return layerKeys.reverse().find((layerIndex) => layerIndex < currentLayer);
}

interface UsePartNavigationParams {
  viewer: any;
  currentLoadingPlan: any;
  layerData: { [key: number]: LayerData };
  scenePartsMap: { [partId: string]: THREE.Object3D };
  palletChildrenMap: { [palletId: string]: string[] };
  isLoadingParts: boolean;
  isEditMode: boolean;
  setPartColor: (part: THREE.Object3D, color: number) => void;
  currentPartRef: React.MutableRefObject<THREE.Object3D | null>;
  hasInitializedVisibilityRef: React.MutableRefObject<boolean>;
  restoreToPartIdRef: React.MutableRefObject<string | null>;
  replacementAcceptedRef: React.MutableRefObject<boolean>;
  lastBatchFirstPartIdRef: React.MutableRefObject<string | null>;
  submitRenestTriggerRef: React.MutableRefObject<((trigger: any) => Promise<void>) | null>;
  isStartFromFirstPart: boolean;
  onStepChange?: (currentStep: number, totalSteps: number) => void;
  handleNextStepCallback?: () => void;
  handlePrevStepCallback?: () => void;
  navigationCommand?: 'next' | 'prev' | null;
  onNavigationComplete?: () => void;
  // Owned at the ViewerComponent level (not here) because usePartLoading's resetSceneForReload
  // also needs these setters, and usePartLoading is wired before this hook.
  currentStep: number;
  setCurrentStep: (step: number) => void;
  totalSteps: number;
  setTotalSteps: (steps: number) => void;
  currentLayer: number;
  setCurrentLayer: (layer: number) => void;
  activePartId: string | null;
  setActivePartId: (partId: string | null) => void;
}

export function usePartNavigation({
  viewer,
  currentLoadingPlan,
  layerData,
  scenePartsMap,
  palletChildrenMap,
  isLoadingParts,
  isEditMode,
  setPartColor,
  currentPartRef,
  hasInitializedVisibilityRef,
  restoreToPartIdRef,
  replacementAcceptedRef,
  lastBatchFirstPartIdRef,
  submitRenestTriggerRef,
  isStartFromFirstPart,
  onStepChange,
  handleNextStepCallback,
  handlePrevStepCallback,
  navigationCommand,
  onNavigationComplete,
  currentStep,
  setCurrentStep,
  totalSteps,
  setTotalSteps,
  currentLayer,
  setCurrentLayer,
  activePartId,
  setActivePartId,
}: UsePartNavigationParams) {
  const [isStepChanging, setIsStepChanging] = useState(false);

  const isCurrentPartSupport = useMemo(() => {
    if (!activePartId) return true;
    return isSupportPartId(activePartId);
  }, [activePartId]);

  const currentPartNestedInfo = useMemo(() => {
    if (!activePartId) return null;
    return currentLoadingPlan?.nestingOutput?.nested_parts?.find((p: any) => p.part_id === activePartId) ?? null;
  }, [activePartId, currentLoadingPlan?.nestingOutput?.nested_parts]);

  const currentPartDbMeta = useMemo(() => {
    if (!activePartId) return null;
    return (
      currentLoadingPlan?.includedParts?.find(
        (p: any) => p.globalId === activePartId || `${p.sanitizedName}_${p.globalId}` === activePartId
      ) ?? null
    );
  }, [activePartId, currentLoadingPlan?.includedParts]);

  // Makes `partObj` the active/focused part: highlighted color, visible, and tracked as current.
  const focusPart = useCallback((partObj: THREE.Object3D, partId: string) => {
    partObj.visible = true;
    setPartColor(partObj, EDIT_ACTIVE_COLOR);
    partObj.updateMatrixWorld(true);
    currentPartRef.current = partObj;
    setActivePartId(partObj.uuid);
    setPalletChildrenVisibility(partId, true, scenePartsMap, palletChildrenMap);
  }, [setPartColor, scenePartsMap, palletChildrenMap, currentPartRef]);

  // Shows or hides the dunnage of every layer in (afterLayer, upToLayer], so stepping over a
  // dunnage-only layer toggles its dunnage too, the same as revealUpToPart does.
  const setSupportObjectsVisibility = useCallback((afterLayer: number, upToLayer: number, visible: boolean) => {
    Object.keys(layerData).map(Number).forEach((layerIndex) => {
      if (layerIndex <= afterLayer || layerIndex > upToLayer) return;
      layerData[layerIndex]?.supportObjects?.forEach((supportObj) => {
        const supportObjInScene = scenePartsMap[supportObj.part_id];
        if (supportObjInScene) {
          supportObjInScene.visible = visible;
          supportObjInScene.updateMatrixWorld(true);
        }
      });
    });
  }, [layerData, scenePartsMap]);

  const resetToFirstPart = useCallback(() => {
    const layerKeys = Object.keys(layerData).map(Number).sort((a, b) => a - b);
    const firstLayerIndex = getPopulatedLayerKeys(layerData)[0];
    if (firstLayerIndex === undefined) return;
    const firstLayer = layerData[firstLayerIndex];

    layerKeys.forEach((layerIndex) => {
      const layer = layerData[layerIndex];
      if (!layer) return;

      layer.parts?.forEach((part) => {
        const partObj = scenePartsMap[part.part_id];
        if (partObj) {
          partObj.visible = false;
          partObj.updateMatrixWorld(true);
          setPalletChildrenVisibility(part.part_id, false, scenePartsMap, palletChildrenMap);
        }
      });

      layer.supportObjects?.forEach((supportObj) => {
        const supportObjInScene = scenePartsMap[supportObj.part_id];
        if (supportObjInScene) {
          supportObjInScene.visible = false;
          supportObjInScene.updateMatrixWorld(true);
        }
      });
    });

    setSupportObjectsVisibility(-Infinity, firstLayerIndex, true);

    const firstPart = scenePartsMap[firstLayer.parts[0].part_id];
    if (firstPart) {
      focusPart(firstPart, firstLayer.parts[0].part_id);
    }

    setCurrentLayer(firstLayerIndex);
  }, [layerData, scenePartsMap, palletChildrenMap, focusPart, setSupportObjectsVisibility]);

  // Shows all parts up to target index; returns flat step index.
  const revealUpToPart = useCallback((targetLayerIndex: number, targetIndexInLayer: number): number => {
    const layerKeys = Object.keys(layerData).map(Number).sort((a, b) => a - b);
    let stepsBefore = 0;

    layerKeys.forEach((layerIndex) => {
      const layer = layerData[layerIndex];
      if (!layer) return;

      const isBeforeTargetLayer = layerIndex < targetLayerIndex;
      const isTargetLayer = layerIndex === targetLayerIndex;

      layer.supportObjects?.forEach((supportObj) => {
        const supportObjInScene = scenePartsMap[supportObj.part_id];
        if (supportObjInScene) {
          supportObjInScene.visible = isBeforeTargetLayer || isTargetLayer;
          supportObjInScene.updateMatrixWorld(true);
        }
      });

      layer.parts?.forEach((part, index) => {
        const partObj = scenePartsMap[part.part_id];
        if (!partObj) return;

        const isVisible = isBeforeTargetLayer || (isTargetLayer && index <= targetIndexInLayer);
        partObj.visible = isVisible;
        partObj.updateMatrixWorld(true);
        setPalletChildrenVisibility(part.part_id, isVisible, scenePartsMap, palletChildrenMap);

        if (isVisible) {
          const isCurrent = isTargetLayer && index === targetIndexInLayer;
          setPartColor(partObj, isCurrent ? EDIT_ACTIVE_COLOR : getIdlePartColor(part.part_id));
        }
      });

      if (isBeforeTargetLayer) {
        stepsBefore += layer.parts?.length ?? 0;
      }
    });

    const targetPart = layerData[targetLayerIndex]?.parts?.[targetIndexInLayer];
    if (targetPart) {
      const partObj = scenePartsMap[targetPart.part_id];
      if (partObj) {
        currentPartRef.current = partObj;
        setActivePartId(partObj.uuid);
      }
    }

    setCurrentLayer(targetLayerIndex);
    return stepsBefore + targetIndexInLayer;
  }, [layerData, scenePartsMap, palletChildrenMap, setPartColor, currentPartRef]);

  const handleNextStep = useCallback(async () => {
    if (!viewer || !currentPartRef.current) return;
    const currentLayerData = layerData[currentLayer];
    if (!currentLayerData || !currentLayerData.parts) return;

    let newStep: number;

    if (currentStep < totalSteps - 1) {
      newStep = currentStep + 1;

      const currentPartIndexInLayer = currentLayerData.parts.findIndex(
        p => p.part_id === currentPartRef.current?.uuid
      );

      const currentPartInScene = currentPartRef.current;
      if (currentPartInScene) {
        currentPartInScene.visible = true;
        setPartColor(currentPartInScene, getIdlePartColor(currentPartInScene.uuid));
        currentPartInScene.updateMatrixWorld(true);
        setPalletChildrenVisibility(currentPartInScene.uuid, true, scenePartsMap, palletChildrenMap);
      }

      const nextPart = currentLayerData.parts[currentPartIndexInLayer + 1];
      let newPartId: string | undefined;

      if (nextPart) {
        const nextPartObj = scenePartsMap[nextPart.part_id];
        if (nextPartObj) {
          focusPart(nextPartObj, nextPart.part_id);
          newPartId = nextPartObj.uuid;
        }
      } else {
        const nextLayerIndex = findAdjacentPopulatedLayer(layerData, currentLayer, 1);
        const nextLayer = nextLayerIndex !== undefined ? layerData[nextLayerIndex] : undefined;

        if (nextLayer?.parts?.length && nextLayerIndex !== undefined) {
          setSupportObjectsVisibility(currentLayer, nextLayerIndex, true);

          const firstPartOfNextLayer = scenePartsMap[nextLayer.parts[0].part_id];
          if (firstPartOfNextLayer) {
            focusPart(firstPartOfNextLayer, nextLayer.parts[0].part_id);
            newPartId = firstPartOfNextLayer.uuid;
          }

          setCurrentLayer(nextLayerIndex);
        }
      }

      // Trigger background lookahead when navigating to the start of the latest batch.
      const isTargetPart =
        newPartId &&
        (newPartId === lastBatchFirstPartIdRef.current ||
          (palletChildrenMap[newPartId] && palletChildrenMap[newPartId].includes(lastBatchFirstPartIdRef.current!)));
      if (isTargetPart) {
        lastBatchFirstPartIdRef.current = null;
        void submitRenestTriggerRef.current?.({ type: 'lookahead' });
      }
    } else {
      newStep = 0;
      resetToFirstPart();
    }

    const scene = viewer.getScene();
    const camera = (viewer as any).camera;
    (viewer as any).renderer.render(scene, camera);

    setCurrentStep(newStep);
    onStepChange?.(newStep, totalSteps);
    handleNextStepCallback?.();
    setIsStepChanging(false);
  }, [viewer, currentStep, totalSteps, layerData, currentLayer, onStepChange, handleNextStepCallback, scenePartsMap, palletChildrenMap, setPartColor, resetToFirstPart, focusPart, setSupportObjectsVisibility, currentPartRef, lastBatchFirstPartIdRef, submitRenestTriggerRef]);

  const handlePrevStep = useCallback(() => {
    if (viewer && currentStep > 0 && currentPartRef.current) {
      const currentLayerData = layerData[currentLayer];
      if (!currentLayerData || !currentLayerData.parts) return;

      const prevStep = currentStep - 1;

      const currentPartIndexInLayer = currentLayerData.parts.findIndex(
        p => p.part_id === currentPartRef.current?.uuid
      );

      const currentPartInScene = scenePartsMap[currentPartRef.current.uuid];
      if (currentPartInScene) {
        currentPartInScene.visible = false;
        currentPartInScene.updateMatrixWorld(true);
        setPalletChildrenVisibility(currentPartRef.current.uuid, false, scenePartsMap, palletChildrenMap);
      }

      const prevPartInLayer = currentLayerData.parts[currentPartIndexInLayer - 1];

      if (prevPartInLayer) {
        const prevPartObj = scenePartsMap[prevPartInLayer.part_id];
        if (prevPartObj) {
          focusPart(prevPartObj, prevPartInLayer.part_id);
        }
      } else {
        const prevLayerIndex = findAdjacentPopulatedLayer(layerData, currentLayer, -1);
        setSupportObjectsVisibility(prevLayerIndex ?? currentLayer - 1, currentLayer, false);

        const prevLayer = prevLayerIndex !== undefined ? layerData[prevLayerIndex] : undefined;

        if (prevLayer?.parts?.length && prevLayerIndex !== undefined) {
          const lastPartId = prevLayer.parts[prevLayer.parts.length - 1].part_id;
          const lastPartOfPrevLayer = scenePartsMap[lastPartId];
          if (lastPartOfPrevLayer) {
            focusPart(lastPartOfPrevLayer, lastPartId);
          }

          setCurrentLayer(prevLayerIndex);
        }
      }

      const scene = viewer.getScene();
      const camera = (viewer as any).camera;
      (viewer as any).renderer.render(scene, camera);

      setCurrentStep(prevStep);
      onStepChange?.(prevStep, totalSteps);
      handlePrevStepCallback?.();
    }
  }, [viewer, currentStep, totalSteps, layerData, currentLayer, onStepChange, handlePrevStepCallback, scenePartsMap, palletChildrenMap, setPartColor, focusPart, setSupportObjectsVisibility, currentPartRef]);

  // This sets the initial step/visibility once per scene load. Re-running it (e.g. after
  // exiting edit mode via Cancel/Accept, which also flips isEditMode and re-triggers this
  // effect) would reset the viewer back to the first/last part instead of staying put.
  useEffect(() => {
    if (!viewer) return;

    // Don't reset visibility while in edit mode (including during replacement).
    // The edit/replace flow manages its own visibility.
    if (isEditMode) return;

    // After accepting a replacement, the scene already has the correct visibility state
    // (old part hidden, replacement visible). Don't re-initialize from layerData.
    if (replacementAcceptedRef.current) return;

    if (hasInitializedVisibilityRef.current) return;

    if (!isLoadingParts && Object.keys(layerData).length > 0) {
      const layerKeys = Object.keys(layerData).map(Number).sort((a, b) => a - b);
      if (layerKeys.length === 0) return;

      hasInitializedVisibilityRef.current = true;

      // A renest-triggered reload asks to land back on whatever the user was viewing, instead
      // of jumping to the first/last part like a true first load does.
      const restoreTargetId = restoreToPartIdRef.current;
      restoreToPartIdRef.current = null;

      if (restoreTargetId) {
        let targetLayerIndex: number | null = null;
        let targetIndexInLayer = -1;
        for (const key of layerKeys) {
          const idx = layerData[key]?.parts?.findIndex((part) => part.part_id === restoreTargetId) ?? -1;
          if (idx !== -1) {
            targetLayerIndex = key;
            targetIndexInLayer = idx;
            break;
          }
        }

        if (targetLayerIndex !== null) {
          const step = revealUpToPart(targetLayerIndex, targetIndexInLayer);
          setCurrentStep(step);
          onStepChange?.(step, totalSteps);
          return;
        }
        // The previously-viewed part no longer exists (e.g. it was replaced) - fall through to
        // the default below.
      }

      if (isStartFromFirstPart) {
        resetToFirstPart();
        setCurrentStep(0);
        onStepChange?.(0, totalSteps);
      } else {
        // Start from last part (original behavior)
        const lastLayerIndex = getPopulatedLayerKeys(layerData).pop();
        if (lastLayerIndex === undefined) return;
        const lastLayer = layerData[lastLayerIndex];

        const step = revealUpToPart(lastLayerIndex, lastLayer.parts.length - 1);
        setCurrentStep(step);
        onStepChange?.(step, totalSteps);
      }
    }
  }, [isLoadingParts, scenePartsMap, layerData, viewer, setPartColor, totalSteps, onStepChange, isStartFromFirstPart, palletChildrenMap, resetToFirstPart, revealUpToPart, isEditMode, replacementAcceptedRef, hasInitializedVisibilityRef, restoreToPartIdRef]);

  // Expose navigation functions via component ref or custom event
  useEffect(() => {
    (window as any).viewerNavigation = {
      handleNextStep,
      handlePrevStep,
      currentStep,
      totalSteps,
    };
  }, [totalSteps, handleNextStep, handlePrevStep, currentStep]);

  // Handle navigation commands from parent
  useEffect(() => {
    if (navigationCommand === 'next') {
      handleNextStep();
      onNavigationComplete?.();
    } else if (navigationCommand === 'prev') {
      handlePrevStep();
      onNavigationComplete?.();
    }
  }, [navigationCommand, handleNextStep, handlePrevStep, onNavigationComplete]);

  return {
    isStepChanging,
    isCurrentPartSupport,
    currentPartNestedInfo,
    currentPartDbMeta,
    resetToFirstPart,
    revealUpToPart,
    handleNextStep,
    handlePrevStep,
  };
}
